import { http } from '@/lib/http';

/**
 * A quote is always pending until the customer responds. `send` and `approve`
 * only stamp `sent_at` / `approved_at` — they don't move `status`. Accepting a
 * quote auto-declines its siblings on the same job (server-side).
 */
export type QuoteStatus = 'pending' | 'accepted' | 'declined';

/**
 * Mirrors `repo.QuoteLineItem`. Display-only breakdown rows — the server does
 * not derive `total_amount` from these.
 */
export interface QuoteLineItem {
  description: string;
  amount: number;
}

/** Mirrors `repo.Quote` in the vizitpro-api swagger spec. */
export interface QuoteDto {
  id: string;
  job_id: string;
  /** Denormalized from jobs, populated on the business-wide list. */
  job_title: string | null;
  customer_user_id: string;
  status: QuoteStatus;
  total_amount: number;
  line_items: QuoteLineItem[];
  notes: string | null;
  cdn_url: string | null;
  created_by_user_id: string;
  approved_by_user_id: string | null;
  approved_at: string | null;
  accepted_by_user_id: string | null;
  sent_at: string | null;
  responded_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Request body for `POST /businesses/{business_id}/jobs/{job_id}/quotes` —
 * mirrors `controller.createQuoteRequest`. `total_amount` is required and
 * client-supplied (not derived from `line_items`). The spec also accepts
 * `cdn_url` but the client has no uploader yet, so it's omitted here.
 */
export interface CreateQuoteRequest {
  total_amount: number;
  /** Display-only breakdown, max 100 rows. */
  line_items?: QuoteLineItem[];
  /** Free text shown on the quote, max 5000 chars. */
  notes?: string;
}

/**
 * Request body for `PATCH /businesses/{business_id}/jobs/{job_id}/quotes/{id}`
 * — mirrors `controller.updateQuoteRequest`. Only pending quotes are editable
 * (server returns 409 otherwise — surface that toast rather than pre-checking).
 * Omitting `line_items` leaves the stored list untouched; sending it (even `[]`)
 * replaces it wholesale.
 */
export interface UpdateQuoteRequest {
  total_amount?: number;
  line_items?: QuoteLineItem[];
  notes?: string;
}

/**
 * Request body for `.../quotes/{id}/respond` — mirrors
 * `controller.respondQuoteRequest`. The job's customer accepts or declines.
 */
export interface RespondQuoteRequest {
  status: 'accepted' | 'declined';
}

export interface ListBusinessQuotesFilters {
  status?: QuoteStatus;
  customer_user_id?: string;
  job_id?: string;
}

/**
 * Response of `POST /businesses/{business_id}/jobs/{job_id}/quotes/{id}/share-link`
 * — mirrors `controller.quoteShareLinkResponse`. `url` is the full link to hand
 * to the customer (`{PUBLIC_BASE_URL}/q/{token}`, composed server-side);
 * `token` is the raw token on its own; `expires_at` is RFC 3339 UTC, ~5 days out.
 */
export interface QuoteShareLinkDto {
  url: string;
  token: string;
  expires_at: string;
}

export function businessQuotesQueryKey(
  businessId: string,
  filters: ListBusinessQuotesFilters,
) {
  return ['quotes', businessId, filters] as const;
}

export function jobQuotesQueryKey(businessId: string, jobId: string) {
  return ['jobs', businessId, jobId, 'quotes'] as const;
}

export function quoteQueryKey(businessId: string, jobId: string, quoteId: string) {
  return ['jobs', businessId, jobId, 'quotes', quoteId] as const;
}

/**
 * Calls `GET /businesses/{business_id}/quotes` — every quote across the
 * business's jobs. Caller must be an owner.
 */
export async function listBusinessQuotes(
  businessId: string,
  filters: ListBusinessQuotesFilters = {},
): Promise<QuoteDto[]> {
  const { data } = await http.get<QuoteDto[]>(`/businesses/${businessId}/quotes`, {
    params: filters,
  });
  return data;
}

/** Calls `GET /businesses/{business_id}/jobs/{job_id}/quotes`. */
export async function listJobQuotes(
  businessId: string,
  jobId: string,
  status?: QuoteStatus,
): Promise<QuoteDto[]> {
  const { data } = await http.get<QuoteDto[]>(
    `/businesses/${businessId}/jobs/${jobId}/quotes`,
    { params: status ? { status } : undefined },
  );
  return data;
}

/** Calls `GET /businesses/{business_id}/jobs/{job_id}/quotes/{id}`. */
export async function getJobQuote(
  businessId: string,
  jobId: string,
  quoteId: string,
): Promise<QuoteDto> {
  const { data } = await http.get<QuoteDto>(
    `/businesses/${businessId}/jobs/${jobId}/quotes/${quoteId}`,
  );
  return data;
}

/**
 * Calls `POST /businesses/{business_id}/jobs/{job_id}/quotes`. Caller must be
 * the owner, or an assigned employee granted `create_quotes`.
 */
export async function createJobQuote(
  businessId: string,
  jobId: string,
  body: CreateQuoteRequest,
): Promise<QuoteDto> {
  const { data } = await http.post<QuoteDto>(
    `/businesses/${businessId}/jobs/${jobId}/quotes`,
    body,
  );
  return data;
}

/** Calls `PATCH /businesses/{business_id}/jobs/{job_id}/quotes/{id}`. */
export async function updateJobQuote(
  businessId: string,
  jobId: string,
  quoteId: string,
  body: UpdateQuoteRequest,
): Promise<QuoteDto> {
  const { data } = await http.patch<QuoteDto>(
    `/businesses/${businessId}/jobs/${jobId}/quotes/${quoteId}`,
    body,
  );
  return data;
}

/** Calls `POST /businesses/{business_id}/jobs/{job_id}/quotes/{id}/send`. */
export async function sendJobQuote(
  businessId: string,
  jobId: string,
  quoteId: string,
): Promise<QuoteDto> {
  const { data } = await http.post<QuoteDto>(
    `/businesses/${businessId}/jobs/${jobId}/quotes/${quoteId}/send`,
  );
  return data;
}

/**
 * Calls `POST /businesses/{business_id}/jobs/{job_id}/quotes/{id}/approve`.
 * Owner sign-off — caller must be the business owner.
 */
export async function approveJobQuote(
  businessId: string,
  jobId: string,
  quoteId: string,
): Promise<QuoteDto> {
  const { data } = await http.post<QuoteDto>(
    `/businesses/${businessId}/jobs/${jobId}/quotes/${quoteId}/approve`,
  );
  return data;
}

/**
 * Calls `POST /businesses/{business_id}/jobs/{job_id}/quotes/{id}/share-link`
 * (empty body). Mints a fresh signed, expiring link — revoking any prior active
 * link for the quote — and stamps `sent_at` if it was null. Caller must be the
 * owner, or an assigned employee granted `create_quotes`. The token is shown
 * only once.
 */
export async function createQuoteShareLink(
  businessId: string,
  jobId: string,
  quoteId: string,
): Promise<QuoteShareLinkDto> {
  const { data } = await http.post<QuoteShareLinkDto>(
    `/businesses/${businessId}/jobs/${jobId}/quotes/${quoteId}/share-link`,
  );
  return data;
}

/** Calls `POST /businesses/{business_id}/jobs/{job_id}/quotes/{id}/respond`. */
export async function respondJobQuote(
  businessId: string,
  jobId: string,
  quoteId: string,
  body: RespondQuoteRequest,
): Promise<QuoteDto> {
  const { data } = await http.post<QuoteDto>(
    `/businesses/${businessId}/jobs/${jobId}/quotes/${quoteId}/respond`,
    body,
  );
  return data;
}
